<template>
  <div class="modal modal-open">
    <div class="modal-box">
      <h2 class="text-2xl font-bold">
        {{ isEdit ? 'Update' : 'Create' }} Item
      </h2>
      <div class="form-control">
        <label class="label">
          <span class="label-text">Name</span>
        </label>
        <input
          v-model="itemData.name"
          type="text"
          placeholder="Baseball Hats"
          class="input input-bordered"
        />
      </div>
      <div class="form-control">
        <label class="label">
          <span class="label-text">Price</span>
        </label>
        <input
          v-model="itemData.price"
          type="text"
          placeholder="$10"
          class="input input-bordered"
        />
      </div>
      <div class="form-control">
        <label class="label">
          <span class="label-text">Image</span>
        </label>
        <div v-if="itemData.images && itemData.images[0]" class="mb-2">
          <img
            :src="itemData.images[0]"
            alt="Current image"
            class="w-full h-auto rounded-lg"
          />
        </div>
        <input
          type="file"
          @change="handleImageUpload"
          class="file-input file-input-bordered w-full"
          accept="image/*"
        />
        <div v-if="imageFile" class="text-sm mt-1">
          Size: {{ (imageFile.size / 1024 / 1024).toFixed(2) }} MB
        </div>
        <div v-if="uploading" class="mt-2">
          <progress class="progress w-full"></progress>
        </div>
      </div>
      <div class="form-control">
        <label class="label cursor-pointer">
          <span class="label-text">Visible</span>
          <input v-model="itemData.visible" type="checkbox" class="checkbox" />
        </label>
      </div>
      <div class="modal-action">
        <button class="btn btn-ghost" @click="$emit('close')">Cancel</button>
        <button class="btn btn-primary" :disabled="!canSave" @click="save">
          {{ isEdit ? 'Update' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, toRefs, watch } from 'vue'
import { merchCollection, storage } from '../firebase'
import { addDoc, doc, updateDoc } from 'firebase/firestore'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  item: {
    type: Object,
    default: () => ({ name: '', price: '', images: [''], visible: true }),
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

const { item, isEdit } = toRefs(props)
const itemData = ref({})
const uploading = ref(false)
const imageFile = ref(null)
const { showToast } = useToast()

watch(
  item,
  (newItem) => {
    itemData.value = { ...newItem }
    if (!itemData.value.images || itemData.value.images.length === 0) {
      itemData.value.images = ['']
    }
  },
  { immediate: true, deep: true }
)

const canSave = computed(
  () => itemData.value.name && itemData.value.price && !uploading.value
)

const handleImageUpload = (event) => {
  const file = event.target.files[0]
  if (!file) {
    imageFile.value = null
    return
  }

  const MAX_FILE_SIZE_MB = 5
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    showToast(
      `File is too large. Please select an image smaller than ${MAX_FILE_SIZE_MB}MB.`,
      'error'
    )
    imageFile.value = null
    event.target.value = '' // Clear the file input
    return
  }

  imageFile.value = file
  itemData.value.images[0] = URL.createObjectURL(file)
}

const save = async () => {
  if (uploading.value) return
  uploading.value = true

  try {
    const payload = { ...itemData.value }

    if (imageFile.value) {
      const imageRef = storageRef(
        storage,
        `merch/${Date.now()}_${imageFile.value.name}`
      )
      await uploadBytes(imageRef, imageFile.value)
      const downloadURL = await getDownloadURL(imageRef)
      payload.images = [downloadURL]
    }

    if (isEdit.value) {
      const docRef = doc(merchCollection, payload.id)
      await updateDoc(docRef, payload)
    } else {
      await addDoc(merchCollection, {
        ...payload,
        dateCreated: new Date(),
      })
    }
    showToast('Successfully Saved Item', 'success')
    emit('close')
  } catch (error) {
    console.error('Failed to save item:', error)
    showToast('Failed to save item. See console for details.', 'error')
  } finally {
    uploading.value = false
  }
}
</script>
